import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiUsers, FiMail, FiTrendingUp, FiFileText, FiSearch, FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { RegisterListingHolder, RegisterListingResult, RegisterListingWebUser } from '../../../model/RegisterListingResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { toast } from '../Toast';
import FilterPopover, { FilterOption } from './FilterPopover';
import { DownloadHoldersModal, RefreshRegisterModal, HolderExportRow } from './HoldersModals';

// ShareRegistryProvider enum order (investi.Core/Model/ShareRegistryProvider.cs)
export const providers = ['None', 'Automic', 'Xcend', 'Computershare', 'MUFG', 'Advanced', 'Boardroom'];
export const registerAsAt = (asAt?: string | null) => asAt ? moment(asAt).calendar(null, { sameDay: '[today], D MMMM YYYY, h:mm a', lastDay: '[yesterday], D MMMM YYYY, h:mm a', lastWeek: 'dddd, D MMMM YYYY, h:mm a', sameElse: 'D MMMM YYYY, h:mm a' }) : null;
const states = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
// registry enum names as words: JointIndividual → Joint individual
const typeLabel = (t: string) => t.replace(/([a-z])([A-Z])/g, (_, a: string, b: string) => `${a} ${b.toLowerCase()}`);
// holding-size bands from the filter pop-up (Figma 532:3748), inclusive like the ASX distribution schedule
const sizes: { value: string; label: string; chip: string; range: [number, number] }[] = [
    { value: 'Any', label: 'Any size', chip: 'Any', range: [0, Infinity] },
    { value: '1-1000', label: '1 – 1,000 shares', chip: '1 – 1,000', range: [1, 1000] },
    { value: '1001-5000', label: '1,001 – 5,000 shares', chip: '1,001 – 5,000', range: [1001, 5000] },
    { value: '5001-10000', label: '5,001 – 10,000 shares', chip: '5,001 – 10,000', range: [5001, 10000] },
    { value: '10001-100000', label: '10,001 – 100,000 shares', chip: '10,001 – 100,000', range: [10001, 100000] },
    { value: '100001+', label: 'More than 100,000 shares', chip: '100,000+', range: [100001, Infinity] },
];
interface Filters { query: string; type: string; hasEmail: string; size: string; state: string }
export const pageSizes = [25, 50, 100];

export const pageList = (pages: number, current: number): (number | '…')[] => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const set = new Set([1, 2, 3, current - 1, current, current + 1, pages].filter(p => p >= 1 && p <= pages));
    const out: (number | '…')[] = [];
    Array.from(set).sort((a, b) => a - b).forEach((p, i, arr) => { if (i && p - arr[i - 1] > 1) out.push('…'); out.push(p); });
    return out;
};

// "12 SMITH ST, SYDNEY NSW 2000" → { location: "Sydney NSW", state: "NSW" }; overseas → last part
const parseAddress = (address?: string) => {
    const m = /([A-Z][A-Z' -]*?)\s+(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\s+\d{4}/i.exec(address || '');
    if (m) return { location: `${title(m[1])} ${m[2].toUpperCase()}`, state: m[2].toUpperCase() };
    const parts = (address || '').split(',').map(s => s.trim()).filter(Boolean);
    return { location: parts.length ? title(parts[parts.length - 1]) : '', state: parts.length ? 'Overseas' : '' };
};
const title = (s: string) => s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

interface Entry {
    holderId?: number; name: string; email: string; holderNumber: string; holderType: string; holding: number;
    change: number; // change over result.balanceHistoricalDays in the primary security; ignored until balanceHistoricalDate is set
    location: string; state: string; address: string;
    members?: Entry[]; // holder group aggregate row
    webUser?: RegisterListingWebUser;
}

const AllHolders: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const [result, setResult] = useState<RegisterListingResult | null>(null);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [query, setQuery] = useState('');
    const [type, setType] = useState('All');
    const [hasEmail, setHasEmail] = useState('Any');
    const [size, setSize] = useState('Any');
    const [state, setState] = useState('Any');
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(1);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [refreshOpen, setRefreshOpen] = useState(false);
    const [refreshStarted, setRefreshStarted] = useState(false);
    const [refreshError, setRefreshError] = useState('');
    const refreshOpenRef = useRef(false);
    refreshOpenRef.current = refreshOpen;

    // returns the error message ('' on success)
    const load = async (fetch: () => Promise<RegisterListingResult>) => {
        const [err, res] = await to(fetch());
        const data = res as RegisterListingResult | undefined;
        const message = err || !data?.success ? (err as Error | null)?.message || data?.message || 'Failed to fetch register listing' : '';
        if (message) setError(message);
        else { setError(''); setResult(data!); }
        return message;
    };
    useAsyncEffect(async () => {
        if (!stock) return;
        setResult(null);
        await load(() => api().getRegisterListing(stock.ticker, stock.exchange));
    }, [stock?.id]);

    const provider = providers[stock?.shareRegistryProvider ?? 0] ?? 'registry';

    // the Refresh modal (HoldersModals.tsx) confirms, then shows progress; the call itself is unchanged
    const openRefresh = () => {
        if (!refreshing) setRefreshStarted(false);
        setRefreshOpen(true);
    };
    const refresh = async () => {
        if (!stock) return;
        setRefreshStarted(true);
        setRefreshError('');
        setRefreshing(true);
        const failed = await load(() => api().refreshRegisterListing(stock.ticker, stock.exchange));
        setRefreshing(false);
        setRefreshError(failed);
        // closed with "Run in Background": the notification is the only sign it finished
        if (refreshOpenRef.current) return;
        if (failed) toast.error(`Refresh from ${provider} didn’t finish: ${failed}`);
        else toast.success(`Register refreshed from ${provider}`);
    };

    // only the primary listed security — options etc. are visible on the holder details page
    const primaryIndex = Math.max((result?.securityCodes || []).indexOf(stock?.ticker || ''), 0);

    // only holders of the primary security: the listing also carries sold-out and options-only holders
    const holders = useMemo(() => (result?.holders || []).filter(h => (h.balances?.[primaryIndex] ?? 0) > 0), [result, primaryIndex]);

    // same shape as v1 RegisterListing: holders sharing a holderGroup collapse into one aggregate row,
    // non-holder web users sit at the bottom
    const { entries, total } = useMemo(() => {
        const toEntry = (h: RegisterListingHolder): Entry => ({
            holderId: h.holderId, name: h.name, email: h.email, holderNumber: h.holderNumber, holderType: h.holderType,
            holding: h.balances?.[primaryIndex] ?? 0, ...parseAddress(h.address), address: h.address || '',
            change: (h.balances?.[primaryIndex] ?? 0) - (h.balanceHistorical ?? 0),
        });
        const groups = new Map<string, Entry[]>();
        const singles: Entry[] = [];
        holders.forEach(h => {
            if (!h.holderGroup) { singles.push(toEntry(h)); return; }
            groups.set(h.holderGroup, [...(groups.get(h.holderGroup) || []), toEntry(h)]);
        });
        const grouped: Entry[] = Array.from(groups.entries()).map(([name, members]) => ({
            name, email: '', holderNumber: '', holderType: `Group · ${members.length} holders`, location: '', state: '', address: '',
            holding: members.reduce((s, m) => s + m.holding, 0),
            change: members.reduce((s, m) => s + m.change, 0),
            members: members.sort((a, b) => b.holding - a.holding),
        }));
        const ranked = [...singles, ...grouped].sort((a, b) => b.holding - a.holding);
        const webUsers: Entry[] = (result?.webUsers || []).map(w => ({
            name: w.name || w.email, email: w.email, holderNumber: '', holderType: 'Web user', holding: 0, change: 0, location: '', state: '', address: '', webUser: w,
        }));
        return { entries: [...ranked, ...webUsers], total: ranked.reduce((s, x) => s + x.holding, 0) };
    }, [holders, result]);

    const holderTypes = useMemo(() => Array.from(new Set(holders.map(h => h.holderType).filter(Boolean))).sort(), [holders]);

    // one predicate for the list and for the pop-ups' live counts; `skip` leaves out the filter whose options are being counted
    const matches = (e: Entry, f: Filters, skip?: keyof Filters) => {
        const q = f.query.trim().toLowerCase();
        const text = (x: Entry) => [x.name, x.email, x.holderNumber].some(c => (c || '').toLowerCase().includes(q));
        const [lo, hi] = (sizes.find(s => s.value === f.size) ?? sizes[0]).range;
        return (!q || text(e) || (e.members || []).some(text)) &&
            (skip === 'type' || f.type === 'All' || e.holderType === f.type || (e.members || []).some(m => m.holderType === f.type)) &&
            (skip === 'hasEmail' || f.hasEmail === 'Any' || (f.hasEmail === 'Yes') === !!(e.email || (e.members || []).some(m => m.email))) &&
            (skip === 'size' || f.size === 'Any' || (e.holding >= lo && e.holding <= hi)) &&
            (skip === 'state' || f.state === 'Any' || e.state === f.state || (e.members || []).some(m => m.state === f.state));
    };
    const filters: Filters = { query, type, hasEmail, size, state };
    const filtersOn = !!query.trim() || type !== 'All' || hasEmail !== 'Any' || size !== 'Any' || state !== 'Any';
    const filtered = useMemo(() => entries.filter(e => matches(e, filters)), [entries, query, type, hasEmail, size, state]); // eslint-disable-line react-hooks/exhaustive-deps

    // live count per option: the other filters applied, this one set to the option
    const optionsFor = (key: keyof Filters, values: { value: string; label: string; chip?: string }[]): FilterOption[] => {
        const base = entries.filter(e => matches(e, filters, key));
        return values.map(v => ({ ...v, count: base.filter(e => matches(e, { ...filters, [key]: v.value })).length }));
    };

    // rank is fixed by holding on the unfiltered list so it survives filtering
    const rank = useMemo(() => new Map(entries.map((e, i) => [e, i + 1])), [entries]);
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const current = Math.min(page, pages);
    const rows = filtered.slice((current - 1) * pageSize, current * pageSize);
    const resetPage = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

    const holderCount = result?.holderCount ?? holders.length;
    const weekAgo = result?.holderCountWeekAgo;
    const delta = weekAgo ? holderCount - weekAgo : null;
    const withEmail = holders.filter(h => h.email).length;
    const top20 = total ? entries.filter(e => !e.webUser).slice(0, 20).reduce((s, e) => s + e.holding, 0) * 100 / total : 0;
    const asAt = registerAsAt(result?.asAt);

    // Download modal rows: one line per holder record (group members carry the group name), web users left out
    const exportRows = (list: Entry[]): HolderExportRow[] => {
        const row = (x: Entry, group: string, r: number | ''): HolderExportRow => ({
            rank: r, name: x.name, holderNumber: x.holderNumber, holderType: x.holderType, group, location: x.location,
            holding: x.holding, pct: total ? x.holding * 100 / total : 0, email: x.email, address: x.address,
        });
        return ([] as HolderExportRow[]).concat(...list.filter(e => !e.webUser)
            .map(e => e.members ? e.members.map(m => row(m, e.name, rank.get(e) ?? '')) : [row(e, '', rank.get(e) ?? '')]));
    };

    const chip = (key: keyof Filters, label: string, heading: string, value: string, values: { value: string; label: string; chip?: string }[], set: (v: string) => void) =>
        <FilterPopover label={label} heading={heading} value={value} options={optionsFor(key, values)} onApply={resetPage(set)} />;
    const plain = (values: string[]) => values.map(v => ({ value: v, label: v }));
    // no share price source yet, so the unmarketable-parcel band (under A$500) can't be worked out
    const sizeOptions: FilterOption[] = [...optionsFor('size', sizes),
        { value: 'unmarketable', label: 'Unmarketable parcel (under A$500)', count: null, disabled: true, hint: 'Not connected yet' }];

    // group aggregate rows have no holder of their own; members and web users open the holder page
    const open = (e: Entry) => e.webUser ? history.push(`/share-registry/holder/web?email=${encodeURIComponent(e.email)}`)
        : e.holderId ? history.push(`/share-registry/holder/${e.holderId}`) : undefined;
    // tour = the guided tour's "click a name" step points at this row (the first on the page)
    const Row = ({ e, member, tour }: { e: Entry; member?: boolean; tour?: boolean }) => <div className={`row${member ? ' member' : ''}${e.webUser ? ' web-user' : ''}${e.members ? '' : ' clickable'}`}
        data-tour={tour ? 'registry-holder-row' : undefined} onClick={() => open(e)}>
        <span className="rank">{member ? '' : rank.get(e)}</span>
        <div className="holder">
            <div className="name">{member && <span className="arrow">↳ </span>}{e.name}</div>
            <div className="sub">{[typeLabel(e.holderType), e.holderNumber, e.location].filter(Boolean).join(' · ')}</div>
        </div>
        <span className={`email${e.email ? '' : ' none'}`}>{e.email ? <><FiMail /> {e.email}</> : e.members ? '' : 'No email on file'}</span>
        <span className="num">{e.webUser || member ? '' : e.holding.toLocaleString()}</span>
        <span className="num">{e.webUser || member ? '' : `${(total ? e.holding * 100 / total : 0).toFixed(2)}%`}</span>
        <span>{!e.webUser && !member && (result?.balanceHistoricalDate
            ? <span className={`pill${e.change > 0 ? ' ok' : e.change < 0 ? ' bad' : ''}`}>{e.change > 0 ? '+' : ''}{e.change.toLocaleString()}</span>
            : <span className="pill">—</span>)}</span>
        <span>{!e.webUser && !member && <span className="pill">TBA</span>}</span>
    </div>;

    return <div className="all-holders">
        <div className="page-header">
            <div>
                <h1>Share Registry</h1>
                <p>{[stock?.companyName, provider !== 'None' && provider, asAt && `Register as at ${asAt}`].filter(Boolean).join(' · ')}</p>
            </div>
            <div className="all-holders-actions">
                <button type="button" className="btn-outline" data-tour="registry-download" onClick={() => setDownloadOpen(true)} disabled={!result}><FiFileText /> Download This List</button>
                <button type="button" className="btn-primary" onClick={openRefresh} disabled={!result}>
                    <FiRefreshCw className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing…' : `Refresh from ${provider}`}
                </button>
            </div>
        </div>

        {downloadOpen && stock && <DownloadHoldersModal stock={stock} listing={result} all={exportRows(entries)} filtered={exportRows(filtered)}
            filtersOn={filtersOn} onClose={() => setDownloadOpen(false)} />}
        {refreshOpen && <RefreshRegisterModal provider={provider} lastRefresh={asAt} holderCount={result ? holderCount : null}
            refreshing={refreshing} started={refreshStarted} error={refreshError} onRefresh={refresh} onClose={() => setRefreshOpen(false)} />}

        {error && <p className="error">{error}</p>}
        {!error && !result && <p className="loading">Loading register listing… (first load can take a minute while reports generate)</p>}

        {result && <>
            <div className="stats" data-tour="registry-stats">
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiUsers /></span> Total holders
                        {delta !== null && <span className={`pill ${delta >= 0 ? 'ok' : 'bad'}`}>{delta >= 0 ? '+' : ''}{weekAgo ? (delta * 100 / weekAgo).toFixed(1) : 0}%</span>}</div>
                    <div className="v">{holderCount.toLocaleString()}</div>
                    <div className="d">{delta === null ? 'No count from last week yet' : `${delta >= 0 ? '+' : ''}${delta.toLocaleString()} since last week`}</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiMail /></span> Holders with an email</div>
                    <div className="v">{withEmail.toLocaleString()}</div>
                    <div className="d">{holderCount ? Math.round(withEmail * 100 / holderCount) : 0}% you can contact directly</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiTrendingUp /></span> Top 20 Share holders</div>
                    <div className="v">{top20.toFixed(1)}%</div>
                    <div className="d">of issued capital</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiFileText /></span> Issued capital</div>
                    <div className="v">{total.toLocaleString()}</div>
                    <div className="d">fully paid ordinary shares</div>
                </div>
            </div>

            <div className="card">
                <div className="filters" data-tour="registry-filters">
                    <label className="search"><FiSearch /><input placeholder="Search by name, holder number or email…" value={query} onChange={e => resetPage(setQuery)(e.target.value)} /></label>
                    {chip('type', 'Holder Type', 'Holder type', type, ['All', ...holderTypes].map(v => ({ value: v, label: typeLabel(v) })), setType)}
                    {chip('hasEmail', 'Has Email', 'Has email', hasEmail, plain(['Any', 'Yes', 'No']), setHasEmail)}
                    <FilterPopover label="Holding Size" heading="Holding size" value={size} options={sizeOptions} onApply={resetPage(setSize)} />
                    {chip('state', 'State', 'State', state, plain(['Any', ...states, 'Overseas']), setState)}
                    <span className="count">Showing {filtered.length.toLocaleString()} of {entries.length.toLocaleString()} holders</span>
                </div>

                <div className="table" tabIndex={0} role="region" aria-label="All holders table">
                    <div className="row head">
                        <span>#</span><span>Holder</span><span>Email</span><span className="num">Holding</span><span className="num">% Issued</span><span>{result?.balanceHistoricalDays ?? 30}-day change</span><span>Segment</span>
                    </div>
                    {rows.map((e, i) => <React.Fragment key={e.webUser ? `w:${e.email}` : e.holderNumber || e.name}>
                        <Row e={e} tour={i === 0} />
                        {e.members?.map(m => <Row key={m.holderNumber} e={m} member />)}
                    </React.Fragment>)}
                    {rows.length === 0 && <p className="empty">No holders match</p>}
                </div>

                <div className="footer">
                    <span className="count">Showing {filtered.length ? (current - 1) * pageSize + 1 : 0}–{Math.min(current * pageSize, filtered.length)} of {filtered.length.toLocaleString()} holders</span>
                    <label className="filter rows">
                        <select aria-label="Rows per page" value={pageSize} onChange={e => resetPage(setPageSize)(Number(e.target.value))}>{pageSizes.map(n => <option key={n} value={n}>Rows per page: {n}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <div className="pagination">
                        <button type="button" disabled={current === 1} onClick={() => setPage(current - 1)}>‹</button>
                        {pageList(pages, current).map((p, i) => p === '…' ? <span key={`e${i}`}>…</span>
                            : <button type="button" key={p} className={p === current ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>)}
                        <button type="button" disabled={current === pages} onClick={() => setPage(current + 1)}>›</button>
                    </div>
                </div>
            </div>
        </>}
    </div>;
};

export default AllHolders;
